/**
 * The focused API service for filing cases — an RTK Query slice.
 *
 * This is the transport half of what used to be `api/submitCase.ts`: it speaks the
 * contract (contracts.ts), owns fetch, payload pruning, envelope reading, the retry
 * policy and the idempotency key, and nothing else. Contracts, user messages and the
 * feature's recovery live elsewhere (contracts.ts, errors.ts, the wizard slice).
 *
 * The idempotency key is minted here, in the browser, once per submit attempt, and
 * reused unchanged across every retry of that attempt. That placement is the whole
 * point: a key generated server-side would be new on each retry, so a lost response
 * would file a second case.
 *
 * The endpoints are mutations, so RTK Query holds nothing after they settle — and the
 * wizard slice resets each entry the moment it has read the result, so the sensitive
 * case payload never lingers in the store (see domesticWizardSlice.ts).
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { fakeBaseQuery } from '@reduxjs/toolkit/query';
import { apiAuthorizationHeaders } from '@/config/apiAuth';
import type {
  AttachmentBatch,
  DomesticCaseForm,
  LensApiResponse,
  SubmittedCase,
} from './contracts';
import { type ApiFailure, attachmentFailure, isApiFailure, submitFailure } from './errors';

const CASES_URL = '/api/v1/cases';
const IDEMPOTENCY_KEY_HEADER = 'X-Idempotency-Key';

/** How many times a transport failure is retried under the same key. */
const MAX_ATTEMPTS = 3;

const RETRY_BASE_DELAY_MS = 400;

/**
 * The codes worth re-presenting the same key for.
 *
 * Both mean "we could not find out what happened", not "this request is wrong". Because
 * the key is unchanged, CMS answers a retry with the original case if the first attempt
 * did land, so a retry cannot file twice. Every other code is an answer, and asking the
 * same rejected question again would only produce it a second time.
 */
const RETRYABLE_CODES: ReadonlySet<string> = new Set(['DownstreamUnavailable', 'Timeout']);

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Reads whichever shape the API answered with.
 *
 * Failures the caller can act on are promoted to a real 4xx carrying RFC 9457
 * `ProblemDetails`; everything else rides a 200 envelope. Both carry the error code, so
 * this collapses the two into one, and the caller never has to know which arrived.
 * Nothing else from either body is read: `detail` and `title` are prose, and prose is
 * not the contract.
 */
async function readError(response: Response): Promise<{ code?: string; target?: string }> {
  try {
    const body = (await response.json()) as
      | LensApiResponse<unknown>
      | { code?: string; extensions?: { code?: string } };

    if ('error' in body && body.error !== undefined) {
      return { code: body.error.code, target: body.error.target };
    }

    if ('code' in body && typeof body.code === 'string') {
      return { code: body.code };
    }

    if ('extensions' in body && typeof body.extensions?.code === 'string') {
      return { code: body.extensions.code };
    }

    return {};
  } catch {
    return {};
  }
}

/**
 * Strips fields the user left alone.
 *
 * An untouched optional field and a field deliberately cleared are the same thing to
 * this form, and neither is an assertion about the case. Sending `""` or `[]` would
 * make it one — an empty string is a value CMS length-checks and stores, so a blank
 * box would become a blank answer on the case record rather than no answer.
 */
function pruneEmpty(form: DomesticCaseForm): Record<string, unknown> {
  const body: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(form)) {
    if (value === undefined || value === null) {
      continue;
    }

    if (typeof value === 'string' && value.trim() === '') {
      continue;
    }

    if (Array.isArray(value) && value.length === 0) {
      continue;
    }

    body[key] = value;
  }

  return body;
}

async function postOnce(form: DomesticCaseForm, idempotencyKey: string): Promise<SubmittedCase> {
  // Outside any transport handling: a call that cannot carry a token fails as
  // Unauthenticated, which the retry loop does not retry.
  const authorization = await apiAuthorizationHeaders(() => submitFailure('Unauthenticated', 0));

  const response = await fetch(CASES_URL, {
    method: 'POST',
    headers: {
      ...authorization,
      'Content-Type': 'application/json',
      [IDEMPOTENCY_KEY_HEADER]: idempotencyKey,
    },
    body: JSON.stringify(pruneEmpty(form)),
  });

  if (response.ok) {
    const envelope = (await response.json()) as LensApiResponse<SubmittedCase>;

    if (envelope.isSuccess && envelope.data !== undefined) {
      return envelope.data;
    }

    // A 200 carrying a failure: the outcome is not something the caller can act on, so
    // it was deliberately not given an error status.
    throw submitFailure(
      (envelope.error?.code ?? 'Unknown') as ApiFailure['code'],
      response.status,
      envelope.error?.target,
    );
  }

  const { code, target } = await readError(response);

  throw submitFailure(
    (code ?? (response.status === 401 ? 'Unauthenticated' : 'Unknown')) as ApiFailure['code'],
    response.status,
    target,
  );
}

/**
 * Files a case, retrying under the same key where a retry can help.
 *
 * A reload counts as a new attempt and so mints a new key. That is deliberate: the
 * user has restarted the submission, and a fresh key is the only way to say so
 * without the portal keeping state, which V3 does not do.
 */
async function submitCaseTransport(form: DomesticCaseForm): Promise<SubmittedCase> {
  const idempotencyKey = crypto.randomUUID();

  let lastError: ApiFailure | undefined;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await postOnce(form, idempotencyKey);
    } catch (error) {
      if (isApiFailure(error) && !RETRYABLE_CODES.has(error.code)) {
        throw error;
      }

      lastError = isApiFailure(error) ? error : submitFailure('TransportFailure', 0);

      // eslint-disable-next-line no-await-in-loop
      await delay(RETRY_BASE_DELAY_MS * 2 ** attempt);
    }
  }

  throw lastError ?? submitFailure('TransportFailure', 0);
}

/**
 * Attaches files to a case that has already been filed.
 *
 * CMS keeps attachments on a separate surface from the case (A1), so this is a second
 * call made only after the case has been filed. It carries no idempotency key: unlike
 * the case, a re-sent file is a genuinely new attachment, not a replay to be
 * de-duplicated, so this is not retried on transport failure — a partial upload left
 * behind is visible and correctable, a silently doubled instrument is not.
 */
async function uploadAttachmentsTransport(
  caseId: string,
  files: readonly File[],
): Promise<AttachmentBatch> {
  if (files.length === 0) {
    return { attached: [], failed: [] };
  }

  const form = new FormData();
  for (const file of files) {
    form.append('files', file, file.name);
  }

  const authorization = await apiAuthorizationHeaders(() =>
    attachmentFailure('Unauthenticated', caseId),
  );

  let response: Response;
  try {
    response = await fetch(`${CASES_URL}/${encodeURIComponent(caseId)}/attachments`, {
      method: 'POST',
      headers: authorization,
      body: form,
    });
  } catch {
    throw attachmentFailure('TransportFailure', caseId);
  }

  if (response.ok) {
    const envelope = (await response.json()) as LensApiResponse<AttachmentBatch>;

    if (envelope.isSuccess && envelope.data !== undefined) {
      return envelope.data;
    }

    throw attachmentFailure((envelope.error?.code ?? 'Unknown') as ApiFailure['code'], caseId);
  }

  const { code } = await readError(response);

  throw attachmentFailure(
    (code ?? (response.status === 401 ? 'Unauthenticated' : 'Unknown')) as ApiFailure['code'],
    caseId,
  );
}

/**
 * The cases API service.
 *
 * `fakeBaseQuery` because the two endpoints need the bespoke transport above — the
 * idempotency key, the retry policy and the two-surface upload — not a plain URL fetch.
 * Each endpoint's `queryFn` turns a thrown {@link ApiFailure} into the `error` half of
 * the RTK Query result, which is exactly what `.unwrap()` rejects with.
 */
export const casesApi = createApi({
  reducerPath: 'casesApi',
  baseQuery: fakeBaseQuery<ApiFailure>(),
  endpoints: (builder) => ({
    submitDomesticCase: builder.mutation<SubmittedCase, DomesticCaseForm>({
      queryFn: async (form) => {
        try {
          return { data: await submitCaseTransport(form) };
        } catch (error) {
          return { error: isApiFailure(error) ? error : submitFailure('TransportFailure', 0) };
        }
      },
    }),
    uploadCaseAttachments: builder.mutation<
      AttachmentBatch,
      { caseId: string; files: readonly File[] }
    >({
      queryFn: async ({ caseId, files }) => {
        try {
          return { data: await uploadAttachmentsTransport(caseId, files) };
        } catch (error) {
          return { error: isApiFailure(error) ? error : attachmentFailure('TransportFailure', caseId) };
        }
      },
    }),
  }),
});

export const { useSubmitDomesticCaseMutation, useUploadCaseAttachmentsMutation } = casesApi;
